<template>
    <Head title="Create IT Equipment" />

    <div class="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-secondary-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New IT Equipment</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Add new IT equipment to your inventory</p>
                        </div>
                        <InfoButton title="Creating IT Equipment" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Create IT Equipment page allows you to register IT equipment into your system.
                                Equipment can be computers, laptops, monitors, printers, networking devices, or any IT asset
                                that needs tracking. Each equipment gets a unique equipment number and can be assigned to
                                multiple users with different roles (primary user, secondary user, admin).
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Basic Information:</strong><br>
                                  &nbsp;&nbsp;- Equipment Name: Clear, descriptive name (e.g., "Dell Latitude 5420 Laptop")<br>
                                  &nbsp;&nbsp;- Category: Required classification for reporting and filtering<br>
                                  &nbsp;&nbsp;- Location: Physical location where equipment is located<br>
                                  &nbsp;&nbsp;- Status: Current status (Active, Maintenance, Retired, Disposed)<br>
                                  &nbsp;&nbsp;- Condition: Physical condition assessment (Excellent, Good, Fair, Poor)<br>
                                • <strong>Equipment Details:</strong> Manufacturer, model, serial number, and barcode
                                  for identification<br>
                                • <strong>Financial Information:</strong> Purchase date, cost, and warranty expiry<br>
                                • <strong>Specifications:</strong> Add custom specifications as key-value pairs<br>
                                • <strong>Notes:</strong> Additional context or special considerations
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Use meaningful names</strong> - Include make/model in the name for clarity<br>
                                • <strong>Keep serial numbers accurate</strong> - Essential for warranty claims and tracking<br>
                                • <strong>Barcode field</strong> supports physical asset tagging for quick lookups<br>
                                • <strong>Specifications</strong> - Add RAM, CPU, Storage, or any relevant technical specs<br>
                                • After creation, you can assign users with different roles (primary, secondary, admin)
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('it-equipment.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-secondary-750 border border-gray-300 dark:border-secondary-600 text-gray-700 dark:text-secondary-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-8">
                    <!-- Basic Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Equipment Name -->
                            <div class="md:col-span-2">
                                <label for="name" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Equipment Name <span class="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    v-model="form.name"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.name }"
                                />
                                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">{{ form.errors.name }}</p>
                            </div>

                            <!-- Category -->
                            <div>
                                <label for="category_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Category <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="category_id"
                                    v-model="form.category_id"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.category_id }"
                                >
                                    <option value="">Select a category</option>
                                    <option v-for="category in categories" :key="category.id" :value="category.id">
                                        {{ category.full_path }}
                                    </option>
                                </select>
                                <p v-if="form.errors.category_id" class="mt-1 text-sm text-red-600">{{ form.errors.category_id }}</p>
                            </div>

                            <!-- Location -->
                            <div>
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Location</label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.location_id }"
                                >
                                    <option value="">No location</option>
                                    <option v-for="location in locations" :key="location.id" :value="location.id">
                                        {{ location.path_with_type }}
                                    </option>
                                </select>
                                <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600">{{ form.errors.location_id }}</p>
                            </div>

                            <!-- Status -->
                            <div>
                                <label for="status" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                                    Status <span class="text-red-500">*</span>
                                </label>
                                <select
                                    id="status"
                                    v-model="form.status"
                                    required
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.status }"
                                >
                                    <option value="active">Active</option>
                                    <option value="maintenance">Maintenance</option>
                                    <option value="retired">Retired</option>
                                    <option value="disposed">Disposed</option>
                                </select>
                                <p v-if="form.errors.status" class="mt-1 text-sm text-red-600">{{ form.errors.status }}</p>
                            </div>

                            <!-- Condition -->
                            <div>
                                <label for="condition" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Condition</label>
                                <select
                                    id="condition"
                                    v-model="form.condition"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.condition }"
                                >
                                    <option value="">Not specified</option>
                                    <option value="excellent">Excellent</option>
                                    <option value="good">Good</option>
                                    <option value="fair">Fair</option>
                                    <option value="poor">Poor</option>
                                </select>
                                <p v-if="form.errors.condition" class="mt-1 text-sm text-red-600">{{ form.errors.condition }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Equipment Details -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Equipment Details</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Manufacturer -->
                            <div>
                                <label for="manufacturer" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Manufacturer</label>
                                <input
                                    type="text"
                                    id="manufacturer"
                                    v-model="form.manufacturer"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.manufacturer }"
                                />
                                <p v-if="form.errors.manufacturer" class="mt-1 text-sm text-red-600">{{ form.errors.manufacturer }}</p>
                            </div>

                            <!-- Model -->
                            <div>
                                <label for="model" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Model</label>
                                <input
                                    type="text"
                                    id="model"
                                    v-model="form.model"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.model }"
                                />
                                <p v-if="form.errors.model" class="mt-1 text-sm text-red-600">{{ form.errors.model }}</p>
                            </div>

                            <!-- Serial Number -->
                            <div>
                                <label for="serial_number" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Serial Number</label>
                                <input
                                    type="text"
                                    id="serial_number"
                                    v-model="form.serial_number"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.serial_number }"
                                />
                                <p v-if="form.errors.serial_number" class="mt-1 text-sm text-red-600">{{ form.errors.serial_number }}</p>
                            </div>

                            <!-- Barcode -->
                            <div>
                                <label for="barcode" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Barcode</label>
                                <input
                                    type="text"
                                    id="barcode"
                                    v-model="form.barcode"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.barcode }"
                                />
                                <p v-if="form.errors.barcode" class="mt-1 text-sm text-red-600">{{ form.errors.barcode }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Financial Information -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Purchase Date -->
                            <div>
                                <label for="purchase_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Date</label>
                                <input
                                    type="date"
                                    id="purchase_date"
                                    v-model="form.purchase_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.purchase_date }"
                                />
                                <p v-if="form.errors.purchase_date" class="mt-1 text-sm text-red-600">{{ form.errors.purchase_date }}</p>
                            </div>

                            <!-- Purchase Cost -->
                            <div>
                                <label for="purchase_cost" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Purchase Cost</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    id="purchase_cost"
                                    v-model="form.purchase_cost"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.purchase_cost }"
                                />
                                <p v-if="form.errors.purchase_cost" class="mt-1 text-sm text-red-600">{{ form.errors.purchase_cost }}</p>
                            </div>

                            <!-- Warranty Expiry -->
                            <div>
                                <label for="warranty_expiry_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">Warranty Expiry Date</label>
                                <input
                                    type="date"
                                    id="warranty_expiry_date"
                                    v-model="form.warranty_expiry_date"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.warranty_expiry_date }"
                                />
                                <p v-if="form.errors.warranty_expiry_date" class="mt-1 text-sm text-red-600">{{ form.errors.warranty_expiry_date }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Specifications -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Specifications</h3>
                        <div class="space-y-3">
                            <div v-for="(spec, index) in specifications" :key="index" class="flex gap-3">
                                <input
                                    v-model="spec.key"
                                    type="text"
                                    placeholder="Key (e.g., RAM, CPU, Storage)"
                                    class="flex-1 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                                <input
                                    v-model="spec.value"
                                    type="text"
                                    placeholder="Value (e.g., 16GB, Intel i7, 512GB SSD)"
                                    class="flex-1 rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                                <button
                                    type="button"
                                    @click="removeSpecification(index)"
                                    class="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                                >
                                    Remove
                                </button>
                            </div>
                            <button
                                type="button"
                                @click="addSpecification"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Add Specification
                            </button>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div>
                        <h3 class="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                        <textarea
                            id="notes"
                            v-model="form.notes"
                            rows="4"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.notes }"
                            placeholder="Additional notes or information..."
                        ></textarea>
                        <p v-if="form.errors.notes" class="mt-1 text-sm text-red-600">{{ form.errors.notes }}</p>
                    </div>

                    <!-- User Assignment Info Box -->
                    <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>User Assignment:</strong> Users can be assigned to this equipment after it has been created. Once saved, visit the equipment's detail page to assign users with different roles (primary user, secondary user, or admin). This allows you to track who is using each piece of equipment.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="mt-8 flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-secondary-700">
                    <Link
                        :href="route('it-equipment.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-secondary-750 border border-gray-300 dark:border-secondary-600 text-gray-700 dark:text-secondary-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ form.processing ? 'Creating...' : 'Create Equipment' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    categories: Array,
    locations: Array,
});

const toast = useToast();
const specifications = ref([]);

const form = useSecureForm({
    name: '',
    category_id: '',
    location_id: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    barcode: '',
    status: 'active',
    condition: '',
    purchase_date: '',
    purchase_cost: '',
    warranty_expiry_date: '',
    custom_fields: {},
    notes: '',
});

const addSpecification = () => {
    specifications.value.push({ key: '', value: '' });
};

const removeSpecification = (index) => {
    specifications.value.splice(index, 1);
};

const submit = () => {
    // Convert specifications array to object
    const specsObject = {};
    specifications.value.forEach(spec => {
        if (spec.key && spec.value) {
            specsObject[spec.key] = spec.value;
        }
    });
    form.custom_fields = specsObject;

    form.post(route('it-equipment.store'), {
        onSuccess: () => {
            toast.success('IT Equipment created successfully!');
        },
        onError: () => {
            toast.error('Failed to create equipment. Please check the form for errors.');
        },
    });
};
</script>
